import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { getPuestos, getEmpleados, getZonas, getClientes, createVacante, updateVacante } from './Integracion';

// Coinciden con TH_VACANTE_TIPO
const TIPOS = [
    { code: 1, name: 'Incremento de plantilla' },
    { code: 2, name: 'Nueva creación' },
    { code: 3, name: 'Reemplazo' },
];
const TIPO_REEMPLAZO = 3;
const ID_PUESTO_OPERADOR = 77;
const CLIENTE_DEFAULT = 5; // Cliente para puestos que no son operador

const FORM_INICIAL = {
    idPuesto: null,
    idTipo: null,
    idZonaOperativa: null,
    idCliente: null,
    idUsuarioReemplaza: null,
    confidencial: false,
    descripcion: '',
};

// vacante = null para crear; con datos para editar
const VacanteDialog = ({ visible, vacante, idUsuario, onHide, onSaved }) => {
    const [form, setForm] = useState(FORM_INICIAL);
    const [catalogos, setCatalogos] = useState({ puestos: [], zonas: [] });
    const [clientes, setClientes] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);

    const esReemplazo = form.idTipo === TIPO_REEMPLAZO;
    const esEdicion = Boolean(vacante);

    // Al abrir: formulario vacío o con los datos de la vacante a editar
    useEffect(() => {
        if (!visible) return;
        setError(null);
        setForm(
            vacante
                ? {
                    idPuesto: vacante.idPuesto,
                    idTipo: vacante.idTipo,
                    idZonaOperativa: vacante.idZonaOperativa,
                    idCliente: vacante.idCliente,
                    idUsuarioReemplaza: vacante.idUsuarioReemplaza,
                    confidencial: vacante.confidencial,
                    descripcion: vacante.descripcion ?? '',
                }
                : FORM_INICIAL
        );
    }, [visible, vacante]);
    const esOperador = form.idPuesto === ID_PUESTO_OPERADOR;

    // Catálogos: se cargan la primera vez que se abre el modal
    useEffect(() => {
        if (!visible || catalogos.puestos.length) return;

        Promise.all([getPuestos(), getZonas()])
            .then(([puestos, zonas]) => setCatalogos({ puestos, zonas }))
            .catch(() => setError('No se pudieron cargar los catálogos. Cierra y vuelve a abrir el formulario.'));
    }, [visible]);

    // Empleados a reemplazar: dependen del puesto elegido
    useEffect(() => {
        setEmpleados([]);
        if (!esReemplazo || !form.idPuesto) return;

        getEmpleados({ puesto: form.idPuesto })
            .then(setEmpleados)
            .catch(() => setError('No se pudieron cargar los empleados de este puesto.'));
    }, [form.idPuesto, esReemplazo]);

    // Clientes: dependen de la zona elegida
    useEffect(() => {
        setClientes([]);
        if (!form.idZonaOperativa) return;

        getClientes({ zona: form.idZonaOperativa })
            .then(setClientes)
            .catch(() => setError('No se pudieron cargar los clientes de esta zona.'));
    }, [form.idZonaOperativa]);

    const setCampo = (campo, valor) => setForm({ ...form, [campo]: valor });

    // Operador elige zona y cliente; los demás puestos van al cliente default sin zona
    const cambiarPuesto = (idPuesto) =>
        setForm({
            ...form,
            idPuesto,
            idUsuarioReemplaza: null,
            idZonaOperativa: null,
            idCliente: idPuesto === ID_PUESTO_OPERADOR ? null : CLIENTE_DEFAULT,
        });

    const cambiarTipo = (idTipo) => setForm({ ...form, idTipo, idUsuarioReemplaza: null });

    const cambiarZona = (idZonaOperativa) => setForm({ ...form, idZonaOperativa, idCliente: null });

    const completo =
        form.idPuesto && form.idTipo && form.idCliente && (!esReemplazo || form.idUsuarioReemplaza);

    const cerrar = () => onHide();

    const guardar = async () => {
        setGuardando(true);
        setError(null);
        try {
            const datos = { ...form, descripcion: form.descripcion.trim() || null };
            if (esEdicion) {
                await updateVacante(vacante.id, datos);
            } else {
                await createVacante({ ...datos, idUsuarioSolicitante: idUsuario });
            }
        } catch (e) {
            console.error('Error al guardar vacante:', e);
            setError(e.response?.data?.message ?? 'No se pudo guardar la vacante. Revisa los datos e intenta de nuevo.');
            setGuardando(false);
            return;
        }

        // Fuera del try: un error después de guardar no debe mostrarse como "no se pudo guardar"
        setGuardando(false);
        onSaved(esEdicion ? 'Vacante actualizada' : 'Vacante creada');
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" text onClick={cerrar} disabled={guardando} />
            <Button
                label={esEdicion ? 'Guardar cambios' : 'Crear vacante'}
                icon="pi pi-check"
                onClick={guardar}
                loading={guardando}
                disabled={!completo}
            />
        </div>
    );

    return (
        <Dialog
            header={esEdicion ? `Editar vacante #${vacante.id}` : 'Nueva vacante'}
            visible={visible}
            onHide={cerrar}
            footer={footer}
            style={{ width: '40rem' }}
            breakpoints={{ '640px': '95vw' }}
            modal
            draggable={false}
        >
            {error && <Message severity="error" text={error} className="w-full mb-3" />}

            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label htmlFor="puesto">Puesto *</label>
                    <Dropdown
                        inputId="puesto"
                        value={form.idPuesto}
                        options={catalogos.puestos}
                        optionLabel="name"
                        optionValue="code"
                        onChange={(e) => cambiarPuesto(e.value)}
                        placeholder="Selecciona un puesto"
                        filter
                        className="w-full"
                    />
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="tipo">Tipo *</label>
                    <Dropdown
                        inputId="tipo"
                        value={form.idTipo}
                        options={TIPOS}
                        optionLabel="name"
                        optionValue="code"
                        onChange={(e) => cambiarTipo(e.value)}
                        placeholder="Selecciona un tipo"
                        className="w-full"
                    />
                </div>

                {esReemplazo && (
                    <div className="field col-12">
                        <label htmlFor="reemplaza">Empleado a reemplazar *</label>
                        <Dropdown
                            inputId="reemplaza"
                            value={form.idUsuarioReemplaza}
                            options={empleados}
                            optionLabel="name"
                            optionValue="code"
                            onChange={(e) => setCampo('idUsuarioReemplaza', e.value)}
                            placeholder={form.idPuesto ? 'Selecciona un empleado' : 'Primero selecciona un puesto'}
                            disabled={!form.idPuesto}
                            emptyMessage="No hay empleados activos en este puesto"
                            filter
                            className="w-full"
                        />
                    </div>
                )}

                {esOperador && (
                    <>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="zona">Zona operativa *</label>
                            <Dropdown
                                inputId="zona"
                                value={form.idZonaOperativa}
                                options={catalogos.zonas}
                                optionLabel="name"
                                optionValue="code"
                                onChange={(e) => cambiarZona(e.value)}
                                placeholder="Selecciona una zona"
                                filter
                                className="w-full"
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="cliente">Cliente *</label>
                            <Dropdown
                                inputId="cliente"
                                value={form.idCliente}
                                options={clientes}
                                optionLabel="name"
                                optionValue="code"
                                onChange={(e) => setCampo('idCliente', e.value)}
                                placeholder={form.idZonaOperativa ? 'Selecciona un cliente' : 'Primero selecciona una zona'}
                                disabled={!form.idZonaOperativa}
                                emptyMessage="No hay clientes activos en esta zona"
                                filter
                                className="w-full"
                            />
                        </div>
                    </>
                )}

                <div className="field col-12">
                    <label htmlFor="descripcion">Descripción</label>
                    <InputTextarea
                        id="descripcion"
                        value={form.descripcion}
                        onChange={(e) => setCampo('descripcion', e.target.value)}
                        rows={3}
                        maxLength={500}
                        autoResize
                        className="w-full"
                    />
                    <small className="block text-right text-500">{form.descripcion.length}/500</small>
                </div>

                <div className="field col-12 mb-0">
                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId="confidencial"
                            checked={form.confidencial}
                            onChange={(e) => setCampo('confidencial', e.checked)}
                        />
                        <label htmlFor="confidencial" className="m-0">Vacante confidencial</label>
                    </div>
                    <small className="text-500">
                        Solo la verás tú y las personas con acceso a vacantes confidenciales.
                    </small>
                </div>
            </div>
        </Dialog>
    );
};

export default VacanteDialog;